/**
 * Turning a silent blank page into something that says what happened.
 *
 * ─── WHY ────────────────────────────────────────────────────────────────────
 * Every route in main.ts renders fire-and-forget:
 *
 *   render: ({ id }) => { void renderProjectDetail(main, id, 'dashboard'); }
 *
 * `void` discards the promise, so a throw anywhere inside a render became an
 * unhandled rejection that nothing caught and nothing showed. The shell header
 * stayed on screen, the content area was left empty, and the app looked like it
 * had simply stopped. `boot().catch()` only covers the initial boot, which is
 * why a missing env var produced a tidy "Boot error" card while a render crash
 * produced nothing at all.
 *
 * On 2026-09-17 that cost a screen recording to notice: the page went blank
 * mid-session and the only clue available was a right-click menu, because the
 * error existed solely in a console nobody had open.
 *
 * This does NOT fix whatever threw. It makes whatever throws say so.
 */

export interface FatalReport {
  message: string;
  /** Present only when the thrown value carried one. */
  stack?: string;
}

/**
 * Normalize anything throwable into something displayable.
 *
 * JS lets you throw any value, and rejected promises routinely carry non-Error
 * values (a Supabase error object, a string, undefined from an aborted fetch).
 * `String(err)` on a bare object yields "[object Object]", which is no more
 * useful than the blank page it replaced.
 */
export function describeError(err: unknown): FatalReport {
  if (err instanceof Error) {
    return { message: err.message || err.name || 'Unknown error', stack: err.stack };
  }
  if (typeof err === 'string' && err.trim()) return { message: err };

  // Supabase and fetch errors are plain objects with a message field.
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>;
    const msg = o.message ?? o.error_description ?? o.error ?? o.details;
    if (typeof msg === 'string' && msg.trim()) {
      const code = typeof o.code === 'string' && o.code ? ` (${o.code})` : '';
      return { message: msg + code };
    }
    try {
      const json = JSON.stringify(err);
      if (json && json !== '{}') return { message: json.slice(0, 300) };
    } catch { /* circular */ }
  }

  // null, undefined, {}, a thrown number. Say so rather than "[object Object]".
  return { message: `Unknown error (${typeof err === 'object' ? 'empty object' : String(err)})` };
}

/** Escape for insertion into the error card. */
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

/**
 * The markup shown in place of a crashed view.
 *
 * Deliberately offers a reload: a render crash usually leaves the view's local
 * state half-built, so continuing to click around produces further errors that
 * obscure the first one.
 */
export function fatalMarkup(report: FatalReport): string {
  return `
    <section class="page">
      <div class="boot-card">
        <h1>Something went wrong</h1>
        <div class="status error">${esc(report.message)}</div>
        <p class="muted">This page stopped loading. Reloading usually clears it.</p>
        <button class="btn-edit-project" data-action="fatal-reload">Reload</button>
      </div>
    </section>`;
}
